/**
 * PresenceIndicator Component
 *
 * Displays an avatar stack showing other users currently viewing the same episode.
 * Shows up to 3 avatars with a "+N" badge if there are more viewers.
 */

import React from 'react';
import { Lock } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { UserPresence } from '@/types/presence.types';

type AvatarSize = 'sm' | 'md' | 'lg';

const sizeClasses: Record<AvatarSize, { avatar: string; text: string; lock: string; lockIcon: string }> = {
  sm: { avatar: 'w-6 h-6', text: 'text-[10px]', lock: 'w-3.5 h-3.5', lockIcon: 'h-2 w-2' },
  md: { avatar: 'w-8 h-8', text: 'text-xs', lock: 'w-4 h-4', lockIcon: 'h-2 w-2' },
  lg: { avatar: 'w-10 h-10', text: 'text-xs', lock: 'w-5 h-5', lockIcon: 'h-2.5 w-2.5' },
};

interface PresenceIndicatorProps {
  viewers: UserPresence[];
  currentUserId?: string;
  maxAvatars?: number;
  lockedByUserId?: string; // User ID of the person who has the edit lock
  size?: AvatarSize; // Avatar size variant
}

/**
 * Get initials from a display name
 */
function getInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return displayName.slice(0, 2).toUpperCase();
}

/**
 * Generate a consistent color based on user ID
 */
function getAvatarColor(userId: string): string {
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-orange-500',
    'bg-pink-500',
    'bg-teal-500',
    'bg-indigo-500',
    'bg-red-500',
  ];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

const PresenceIndicator: React.FC<PresenceIndicatorProps> = ({
  viewers,
  currentUserId,
  maxAvatars = 3,
  lockedByUserId,
  size = 'lg',
}) => {
  const sizeStyle = sizeClasses[size];
  // Don't render if no other viewers
  if (!viewers || viewers.length === 0) {
    return null;
  }

  const visibleViewers = viewers.slice(0, maxAvatars);
  const remainingCount = viewers.length - maxAvatars;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center">
            {/* Avatar stack */}
            <div className="flex -space-x-2">
              {visibleViewers.map((viewer, index) => {
                const isCurrentUser = viewer.odId === currentUserId;
                const hasLock = viewer.odId === lockedByUserId;
                const borderColor = isCurrentUser ? 'border-green-500' : 'border-white';
                return (
                  <div
                    key={viewer.odId}
                    className="relative"
                    style={{ zIndex: maxAvatars - index }}
                  >
                    {viewer.photoURL ? (
                      <img
                        src={viewer.photoURL}
                        alt={viewer.displayName}
                        className={`${sizeStyle.avatar} rounded-full border-2 ${borderColor} object-cover`}
                      />
                    ) : (
                      <div
                        className={`${sizeStyle.avatar} rounded-full border-2 ${borderColor} flex items-center justify-center text-white ${sizeStyle.text} font-medium ${getAvatarColor(viewer.odId)}`}
                      >
                        {getInitials(viewer.displayName)}
                      </div>
                    )}
                    {/* Lock icon overlay for user with edit lock */}
                    {hasLock && (
                      <div className={`absolute -bottom-0.5 -right-0.5 ${sizeStyle.lock} rounded-full bg-black/80 border border-white/20 flex items-center justify-center`}>
                        <Lock className={`${sizeStyle.lockIcon} text-cyan-400`} />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* +N badge for remaining viewers */}
              {remainingCount > 0 && (
                <div
                  className={`${sizeStyle.avatar} rounded-full border-2 border-white bg-muted flex items-center justify-center text-muted-foreground ${sizeStyle.text} font-semibold`}
                  style={{ zIndex: 0 }}
                >
                  +{remainingCount}
                </div>
              )}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end">
          <div className="text-sm">
            <p className="font-medium mb-1 text-muted-foreground">Currently viewing:</p>
            <ul className="space-y-0.5">
              {viewers.map((viewer) => {
                const isCurrentUser = viewer.odId === currentUserId;
                return (
                  <li key={viewer.odId} className="flex items-center gap-2">
                    {viewer.photoURL ? (
                      <img
                        src={viewer.photoURL}
                        alt=""
                        className={`w-4 h-4 rounded-full ${isCurrentUser ? 'ring-1 ring-green-500' : ''}`}
                      />
                    ) : (
                      <div
                        className={`w-4 h-4 rounded-full flex items-center justify-center text-white text-[8px] font-medium ${getAvatarColor(viewer.odId)}`}
                      >
                        {getInitials(viewer.displayName)}
                      </div>
                    )}
                    <span>
                      {viewer.displayName}
                      {isCurrentUser && <span className="text-green-500 ml-1">(You)</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default PresenceIndicator;
